import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import { statusLabel, statusTone } from "components/markets/marketActions";
import { PeriodPicker, Tile, TileSkeleton, periodRange } from "components/revenue/RevenueStatement";
import { apiGet, buildQueryString } from "lib/apiClient";
import { formatKes } from "lib/format";
import { useAuth } from "contexts/AuthContext";
import { partnerPageUrls, sourceBreakdown } from "lib/partnerPages";

// No "All time": the endpoint defaults to the last 30 days when `from` is left out.
const PERFORMANCE_PERIODS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "365d", label: "12 months" },
];

const SOURCE_META = {
  PARTNER: { label: "Your page / subdomain", color: "bg-primary" },
  MAIN_SITE: { label: "Wasaa main site", color: "bg-amber-400" },
  OTHER_PARTNER: { label: "Other partners' pages", color: "bg-purple-500" },
};

const numCell = "num whitespace-nowrap py-2 pr-3 text-right text-sm text-navy-700 dark:text-gray-200";

function count(value) {
  return Number(value ?? 0).toLocaleString();
}

function SourceBreakdown({ bySource }) {
  const rows = sourceBreakdown(bySource, "trades");
  const total = rows.reduce((sum, r) => sum + r.trades, 0);
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900" role="img" aria-label="Trades on your markets by where they came from">
        {total > 0 && rows.map((r) => <div key={r.source} className={SOURCE_META[r.source].color} style={{ width: `${r.percent}%` }} />)}
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[480px] text-left">
          <thead>
            <tr className="text-xs text-gray-500">
              <th className="pb-2 pr-3 font-semibold">Where the trade came from</th>
              <th className="pb-2 pr-3 text-right font-semibold">Trades</th>
              <th className="pb-2 pr-3 text-right font-semibold">Share</th>
              <th className="pb-2 pr-3 text-right font-semibold">Volume</th>
              <th className="pb-2 text-right font-semibold">Traders</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} className="border-t border-gray-100 dark:border-white/5">
                <td className="py-2 pr-3 text-sm text-navy-700 dark:text-white">
                  <span className="inline-flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${SOURCE_META[r.source].color}`} />
                    {SOURCE_META[r.source].label}
                  </span>
                </td>
                <td className={numCell}>{count(r.trades)}</td>
                <td className={numCell}>{r.percent.toFixed(1)}%</td>
                <td className={numCell}>{formatKes(r.volume)}</td>
                <td className="num py-2 text-right text-sm text-navy-700 dark:text-gray-200">{count(r.uniqueTraders)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">A trader can trade from more than one place, so the trader counts can add up to more than your total.</p>
    </div>
  );
}

export default function PartnerPerformancePage() {
  const { portal } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const urls = partnerPageUrls(portal?.partner?.slug);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { from, to } = periodRange(period);
    apiGet(`/portal/partner/performance${buildQueryString({ from, to })}`)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load your performance.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const markets = data?.markets || [];
  const via = data?.mainMarketsViaPartner;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-semibold text-navy-700 dark:text-white">Performance</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">How your markets traded, and where those trades came from.</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} periods={PERFORMANCE_PERIODS} />
      </div>

      {error && <div className="rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}

      {loading && !data ? (
        <TileSkeleton count={3} />
      ) : (
        data && (
          <div className={`grid grid-cols-1 gap-3 sm:grid-cols-3 ${loading ? "opacity-60" : ""}`}>
            <Tile label="Volume on your markets" value={formatKes(data.totals.volume)} tone="blue" emphasis />
            <Tile label="Unique traders" value={count(data.totals.uniqueTraders)} />
            <Tile label="Trades" value={count(data.totals.trades)} />
          </div>
        )
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-5">
        <Card extra="p-4 sm:p-5 xl:col-span-3">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Trades by source</p>
          <p className="mb-4 mt-1 text-sm text-gray-600 dark:text-gray-300">Trades on your own markets, by the site the trader placed them on.</p>
          {data ? <SourceBreakdown bySource={data.bySource} /> : <div className="h-40 animate-pulse rounded-md bg-accent" />}
        </Card>

        <Card extra="p-4 sm:p-5 xl:col-span-2">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Trades on Wasaa markets via your page</p>
          <p className="mb-4 mt-1 text-sm text-gray-600 dark:text-gray-300">
            Trades on {portal?.tenant?.name || "Wasaa"}'s own (main) markets placed from your page or subdomain.
          </p>
          {via ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
              <Tile label="Volume" value={formatKes(via.volume)} tone="green" />
              <Tile label="Trades" value={count(via.trades)} />
              <Tile label="Traders" value={count(via.uniqueTraders)} />
            </div>
          ) : (
            <div className="h-24 animate-pulse rounded-md bg-accent" />
          )}
          {urls && (
            <p className="mt-4 text-xs text-gray-500">
              Share{" "}
              <a href={urls.subdomain} target="_blank" rel="noreferrer" className="font-semibold text-brand-500 hover:underline">
                {urls.subdomain.replace("https://", "")}
              </a>{" "}
              so trades are credited to you. You can feature main markets on{" "}
              <Link to="/portal/curated-markets" className="font-semibold text-brand-500">
                your page
              </Link>
              .
            </p>
          )}
        </Card>
      </div>

      <Card extra="p-4 sm:p-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Your markets</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Each of your markets in this period. The source columns count trades.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[820px] text-left">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="pb-2 pr-3 font-semibold">Market</th>
                <th className="pb-2 pr-3 font-semibold">Status</th>
                <th className="pb-2 pr-3 text-right font-semibold">Volume</th>
                <th className="pb-2 pr-3 text-right font-semibold">Traders</th>
                <th className="pb-2 pr-3 text-right font-semibold">Trades</th>
                <th className="pb-2 pr-3 text-right font-semibold">Your page</th>
                <th className="pb-2 pr-3 text-right font-semibold">Main site</th>
                <th className="pb-2 text-right font-semibold">Other partners</th>
              </tr>
            </thead>
            <tbody>
              {!data && !error && (
                <tr>
                  <td colSpan={8} className="py-2">
                    <div className="h-6 animate-pulse rounded-md bg-accent" />
                  </td>
                </tr>
              )}
              {data && markets.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-sm text-gray-500">
                    No trades on your markets in this period.
                  </td>
                </tr>
              )}
              {markets.map((m) => (
                <tr key={m.marketId} className="border-t border-gray-100 align-top dark:border-white/5">
                  <td className="max-w-[320px] py-2 pr-3 text-sm">
                    <Link to={`/portal/markets/${m.marketId}`} className="line-clamp-2 font-medium text-navy-700 hover:text-brand-500 dark:text-white">
                      {m.question}
                    </Link>
                  </td>
                  <td className="py-2 pr-3">
                    <StatusBadge label={statusLabel(m.status)} tone={statusTone(m.status)} />
                  </td>
                  <td className={numCell}>{formatKes(m.volume)}</td>
                  <td className={numCell}>{count(m.uniqueTraders)}</td>
                  <td className={numCell}>{count(m.trades)}</td>
                  <td className={numCell}>{count(m.bySource?.PARTNER)}</td>
                  <td className={numCell}>{count(m.bySource?.MAIN_SITE)}</td>
                  <td className="num py-2 text-right text-sm text-navy-700 dark:text-gray-200">{count(m.bySource?.OTHER_PARTNER)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
